<template>
  <div>
    <RevDivider v-if="isCrossSellVisible" class="my-40 md:my-56" />
    <RecommendationAlgoliaCarousel
      v-if="isAlgoliaRecommendationBoughtTogetherActive"
      :recommendation-query="{
        objectID,
        model: 'bought-together',
        options: {
          maxRecommendations: 4,
          queryParameters: {
            filters: itemsToExclude,
          },
          fallbackParameters: {
            filters: itemsToExclude,
          },
        },
      }"
      :title="i18n(translations.titleCrossSell)"
      :trackingData="{
        list: 'cross sell',
      }"
      :withAddToCart="true"
      @loaded="handleCrossSellLoaded"
      @refresh="emit('added')"
    />
    <RecommendationCarousel
      v-else
      :options="{
        withCrossedPrice: true,
        withStartingFrom: false,
        withGrade: true,
        withAddToCart: true,
      }"
      :recommendation-query="{
        category: 'crossSellWeb',
        limit: 12,
        personalisation: true,
        scope: 'cart',
        scopeId: recommendationIds,
      }"
      :title="i18n(translations.titleCrossSell)"
      :trackingData="{
        list: 'cross sell',
      }"
      @loaded="handleCrossSellLoaded"
      @refresh="emit('added')"
    />

    <RevDivider v-if="isCrossSellVisible" class="my-40 md:my-56" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { CartItem } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import RecommendationAlgoliaCarousel from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import RecommendationCarousel from '@backmarket/nuxt-layer-recommendation/RecommendationCarousel.vue'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevDivider } from '@ds/components/Divider'

import translations from './CrossSell.translations'

const props = defineProps<{
  items: Array<CartItem>
}>()

const emit = defineEmits<{
  added: []
}>()

const i18n = useI18n()
const isCrossSellVisible = ref(false)

const experiments = useExperiments()
const isAlgoliaRecommendationBoughtTogetherActive =
  experiments['experiment.algoliaRecommendBoughtTogether'] === 'Algolia'

const recommendationIds = computed(() =>
  props.items.reduce((acc, item) => `${acc},${item.listingId}`, ''),
)

const objectID = computed(() => getObjectId(findMostExpensiveItem(props.items)))

const itemsToExclude = computed(() =>
  props.items
    .reduce((filterQuery, item) => {
      const id = getObjectId(item)
      if (id !== objectID.value) {
        filterQuery.push(`NOT objectID:${id}`)
      }

      return filterQuery
    }, [] as string[])
    .join(' AND '),
)

const handleCrossSellLoaded = (results: Product[]) => {
  if (isEmpty(results)) {
    isCrossSellVisible.value = false
  } else {
    isCrossSellVisible.value = true
  }
}

function getObjectId(item: CartItem) {
  if (!item.gradeId) return null

  return `${item.link.params.uuid}_${item.gradeId}_0`
}

function findMostExpensiveItem(items: CartItem[]) {
  return items.reduce(
    (lastMostExpensiveItem, currentItem) =>
      lastMostExpensiveItem.price > currentItem.price
        ? lastMostExpensiveItem
        : currentItem,
    items[0],
  )
}
</script>
